import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import { TimeTrackingContainer } from "./TimeTracking.styles";
import TimeTrackingCompanyTable from "./TimeTrackingCompanyTable";
import { employeesApi } from "../../../api/employeesApi";
import { timeTrackingApi } from "../../../api/timeTrackingApi";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { EmployeeUserType } from "../../../types/employeeType";
import { TimeTrackingEntry } from "../../../types/timeTracking";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { ServerSidePagination } from "../../../components/common/table/ServerSidePagination";

type CompanyFilters = {
  employeeUserId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const breadcrumbs = [
  {
    title: "timetracking.breadcrumb",
    link: "timetracking",
  },
];

type TimeTrackingPageProps = {
  serviceModuleId: number;
  hidePageTitle?: boolean;
  fixedEmployeeId?: string;
  hideEmployeeFilter?: boolean;
};

const TimeTrackingPage = ({
  hidePageTitle = false,
  serviceModuleId,
  fixedEmployeeId,
  hideEmployeeFilter = false,
}: TimeTrackingPageProps) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [filters, setFilters] = useState<CompanyFilters>({
    employeeUserId: fixedEmployeeId ?? "all",
    startDate: null,
    endDate: null,
  });

  const paginatedCompanyEntries = useServerSidePagination<TimeTrackingEntry>(
    async (pagination) => {
      const response = await timeTrackingApi.getCompanyEntries(
        serviceModuleId,
        {
          employeeUserId:
            fixedEmployeeId ??
            (filters.employeeUserId !== "all" ? filters.employeeUserId : undefined),
          startTimestamp: filters.startDate?.startOf("day").toISOString(),
          endTimestamp: filters.endDate?.endOf("day").toISOString(),
          offset: pagination.offset,
          limit: pagination.limit,
          search: pagination.search,
          sort: pagination.sort,
          order: pagination.order,
        }
      );

      if (!response.success() || !response.result) {
        return { items: [], totalCount: 0 };
      }

      return response.result;
    },
    [filters, serviceModuleId]
  );

  useEffect(() => {
    setIsLoadingEmployees(true);
    const fetchEmployees = async () => {
      const response = await employeesApi.getServiceModuleEmployees(serviceModuleId);
      if (response.success() && response.result) {
        setEmployees(response.result);
      }
      setIsLoadingEmployees(false);
    };
    fetchEmployees();
  }, []);

  const handleFiltersChange = (partial: Partial<CompanyFilters>) => {
    const sanitized = { ...partial };
    if (fixedEmployeeId) {
      delete sanitized.employeeUserId;
    }
    const newFilters = { ...filters, ...sanitized };
    setFilters(newFilters);
    paginatedCompanyEntries.updateTableItems();
    paginatedCompanyEntries.handleOnPageChange({ ...paginatedCompanyEntries.paginationParameters, offset: 0 });
  };

  const handleClearFilters = () => {
    const defaultFilters: CompanyFilters = {
      employeeUserId: fixedEmployeeId ?? "all",
      startDate: null,
      endDate: null,
    };
    setFilters(defaultFilters);
    paginatedCompanyEntries.updateTableItems();
  };

  const getEmployeeName = useCallback(
    (employeeId: string) => {
      if (employeeId === "") {
        return t("serviceModulesPage.timetrackingPage.table.employeeUnknown");
      }
      const employee = employees.find((item) => item.id === employeeId);
      if (!employee) {
        return employeeId;
      }
      return `${employee.firstName} ${employee.lastName}`.trim();
    },
    [employees, t]
  );

  const isCompanyLoading = paginatedCompanyEntries.isLoading || (isLoadingEmployees && employees.length === 0);

  return (
    <>
      {!hidePageTitle && (
        <PageTitle
          breadcrumbs={breadcrumbs}
          title={t("serviceModulesPage.timetrackingPage.title")}
        />
      )}
      <TimeTrackingContainer>
        <TimeTrackingCompanyTable
          entries={paginatedCompanyEntries.items}
          isLoading={isCompanyLoading}
          filters={filters}
          employees={employees}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          getEmployeeName={getEmployeeName}
          paginatedData={paginatedCompanyEntries}
          hideEmployeeFilter={hideEmployeeFilter}
        />
        <ServerSidePagination paginatedData={paginatedCompanyEntries} />
      </TimeTrackingContainer>
    </>
  );
};

export default TimeTrackingPage;
